import { Link, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  billsApi,
  suppliersApi,
  whtApi,
  fmtCents,
  paymentsApi,
  bankApi,
  type Supplier,
  type BillListItem,
  type BillLineInput,
  VAT_INPUT_CLASSIFICATIONS,
} from "../lib/business";
import { tenantApi } from "../lib/tenant";
import { RupeeInput } from "../lib/RupeeInput";
import { ApiError } from "../lib/api";
import { StatusPill } from "../components/StatusPill";
import { AttachmentList } from "../components/AttachmentList";
import { HistoryDrawer } from "../components/HistoryDrawer";
import { localISOPlusDays, todayLocalISO } from "../lib/dates";

export function BillsPage() {
  return (
    <Routes>
      <Route index element={<BillList />} />
      <Route path="new" element={<BillNew />} />
      <Route path=":id/edit" element={<BillEdit />} />
      <Route path=":id" element={<BillDetail />} />
      <Route path="*" element={<Navigate to="" />} />
    </Routes>
  );
}

function BillList() {
  const [tab, setTab] = useState("all");
  const status = tab === "all" ? undefined : tab;
  const { data, isLoading } = useQuery({
    queryKey: ["bills", tab],
    queryFn: () => billsApi.list(status),
  });
  const tabs = [
    { key: "all", label: "All" },
    { key: "pending_approval", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "paid", label: "Paid" },
    { key: "rejected", label: "Rejected" },
  ];

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Bills &amp; expenses</div>
          <h1 className="stage-title">
            Money <em>out.</em>
          </h1>
          <p className="stage-sub">
            Photograph or upload a receipt, OCR extracts the data; or enter manually. WHT and
            input-VAT claim window handled automatically.
          </p>
        </div>
        <Link to="new" className="btn btn-primary">+ New bill</Link>
      </div>

      <div className="bill-tabs" style={billTabsStyle}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              padding: "7px 16px",
              borderRadius: 100,
              fontSize: 13,
              fontWeight: 500,
              color: tab === t.key ? "var(--ink)" : "var(--ink-3)",
              background: tab === t.key ? "white" : "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card card-pad-lg" style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 24, color: "var(--ink-3)" }}>Loading…</div>
        ) : (data?.bills.length ?? 0) === 0 ? (
          <div
            style={{
              padding: 60,
              margin: 16,
              textAlign: "center",
              color: "var(--ink-3)",
              background: "var(--paper-2)",
              border: "0.5px dashed var(--line)",
              borderRadius: 14,
            }}
          >
            No bills yet.{" "}
            <Link to="new" style={{ color: "var(--accent)", fontWeight: 500 }}>
              Add your first one →
            </Link>
          </div>
        ) : (
          <div className="table-cards table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Number</th>
                <th style={th}>Supplier</th>
                <th style={th}>Issued</th>
                <th style={th}>Due</th>
                <th style={{ ...th, textAlign: "right" }}>Total</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.bills.map((b) => <BillRow key={b.id} b={b} />)}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
}

function BillRow({ b }: { b: BillListItem }) {
  return (
    <tr>
      <td style={td} data-label="Number">
        <Link to={b.id} style={{ color: "var(--accent)", fontWeight: 500 }}>
          {b.bill_number}
        </Link>
      </td>
      <td style={td} data-label="Supplier">{b.supplier.name}</td>
      <td style={td} data-label="Issued">{fmtDate(b.issue_date)}</td>
      <td style={td} data-label="Due">{fmtDate(b.due_date)}</td>
      <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num" data-label="Total">
        {fmtCents(b.total_cents)}
      </td>
      <td style={td} data-label="Status">
        <StatusPill
          status={b.status}
          dueDate={b.due_date}
          paidCents={b.paid_cents}
          totalCents={b.total_cents}
        />
      </td>
    </tr>
  );
}

function BillNew() {
  const navigate = useNavigate();
  const { data: tenant } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const { data: suppliersData } = useQuery({
    queryKey: ["suppliers", "all"],
    queryFn: () => suppliersApi.list(),
  });

  const today = todayLocalISO();
  const [supplierId, setSupplierId] = useState<string>("");
  const [billNumber, setBillNumber] = useState("");
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(localISOPlusDays(30));
  const [notes] = useState("");
  const [taxInvoiceReceived, setTaxInvoiceReceived] = useState(false);
  const [lines, setLines] = useState<BillLineInput[]>([
    { description: "", lineType: "service", quantity: 1, unitPriceCents: 0, vatClassification: "taxable", vatRatePct: tenant?.vat_registered ? 18 : null, ssclApply: false, whtRatePct: null },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Fix #9: sync the initial line's ssclApply/vatRatePct once tenant loads.
  useEffect(() => {
    if (!tenant) return;
    setLines((prev) =>
      prev.map((l) => {
        if (l.unitPriceCents !== 0) return l;
        const carries = l.vatClassification == null
          || l.vatClassification === "taxable"
          || l.vatClassification === "imported";
        return {
          ...l,
          vatRatePct: l.vatRatePct ?? (tenant.vat_registered ? 18 : null),
          ssclApply: carries && !!tenant.sscl_liable && !!tenant.vat_registered,
        };
      }),
    );
  }, [tenant]);

  // WHT rules — used to look up the supplier's default rate.
  const { data: whtRulesData } = useQuery({
    queryKey: ["wht-rules"],
    queryFn: () => whtApi.rules(),
    staleTime: 5 * 60 * 1000,
  });

  // When supplier changes, auto-prefill WHT rate on lines that don't already
  // have one set. Uses supplier.whtCategoryCode → WhtRule.rate_pct.
  const supplierObj = suppliersData?.suppliers.find((s: Supplier) => s.id === supplierId);
  const supplierWhtRate = (() => {
    if (!supplierObj?.whtCategoryCode || !whtRulesData) return null;
    const rule = whtRulesData.rules.find((r) => r.code === supplierObj.whtCategoryCode);
    return rule ? parseFloat(rule.rate_pct) : null;
  })();

  function lineErrors(l: BillLineInput): string[] {
    const errors: string[] = [];
    if (!l.description.trim()) errors.push("Description is required.");
    if (!(l.quantity > 0)) errors.push("Quantity must be greater than zero.");
    if (!(Number(l.unitPriceCents) > 0)) errors.push("Unit price must be greater than zero.");
    const carries = l.vatClassification === "taxable" || l.vatClassification === "imported";
    if (carries && tenant?.vat_registered && (l.vatRatePct == null || l.vatRatePct < 0)) {
      errors.push("Taxable purchase needs a VAT rate (typical: 18%).");
    }
    return errors;
  }
  const anyLineHasErrors = lines.some((l) => lineErrors(l).length > 0);

  function update(i: number, patch: Partial<BillLineInput>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines([...lines, {
      description: "",
      lineType: "service",
      quantity: 1,
      unitPriceCents: 0,
      vatClassification: "taxable",
      vatRatePct: null,
      ssclApply: false,
      whtRatePct: supplierWhtRate, // prefill from supplier default
    }]);
  }
  function onSupplierChange(newId: string) {
    setSupplierId(newId);
    // Best-effort prefill: any line that hasn't had a WHT rate set gets the
    // supplier's default. Lines with an explicit rate stay untouched.
    const supplier = suppliersData?.suppliers.find((s: Supplier) => s.id === newId);
    const code = supplier?.whtCategoryCode;
    const rule = code && whtRulesData?.rules.find((r) => r.code === code);
    const rate = rule ? parseFloat(rule.rate_pct) : null;
    if (rate != null) {
      setLines((prev) =>
        prev.map((l) => (l.whtRatePct == null ? { ...l, whtRatePct: rate } : l)),
      );
    }
  }
  function removeLine(i: number) {
    setLines(lines.filter((_, idx) => idx !== i));
  }

  // SSCL applies before VAT; VAT then compounds on (base + SSCL). Mirror the
  // backend lib/tax.ts so the live preview matches what gets posted.
  const totals = lines.reduce(
    (acc, l) => {
      const qtyT = BigInt(Math.round(l.quantity * 1000));
      const sub = (BigInt(l.unitPriceCents) * qtyT) / 1000n;
      const sscl = l.ssclApply && tenant?.sscl_liable ? (sub * 250n) / 10000n : 0n;
      const vatBase = sub + sscl;
      const vat = l.vatRatePct ? (vatBase * BigInt(Math.round(l.vatRatePct * 100))) / 10000n : 0n;
      const wht = l.whtRatePct ? (sub * BigInt(Math.round(l.whtRatePct * 100))) / 10000n : 0n;
      return { sub: acc.sub + sub, vat: acc.vat + vat, sscl: acc.sscl + sscl, wht: acc.wht + wht, total: acc.total + sub + vat + sscl - wht };
    },
    { sub: 0n, vat: 0n, sscl: 0n, wht: 0n, total: 0n }
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const created = await billsApi.create({
        supplierId,
        billNumber,
        issueDate,
        dueDate,
        currency: tenant?.base_currency ?? "LKR",
        notes: notes || null,
        taxInvoiceReceived,
        lines: lines.map((l) => ({ ...l, unitPriceCents: String(l.unitPriceCents) })),
      });
      navigate(`/bills/${created.id}`);
    } catch (e) {
      // Surface a specific message — Shamil hit "could not save" with no
      // explanation on a duplicate bill number. Always tell the user why.
      if (e instanceof ApiError) {
        const friendly: Record<string, string> = {
          duplicate_bill_number: `Bill number "${billNumber}" is already in use for this supplier. Pick a different number.`,
          supplier_not_found: "Supplier not found. Pick a supplier from the list.",
          wht_threshold_not_crossed: "WHT can't be charged: the supplier hasn't crossed the Rs 100,000 monthly threshold yet. Clear the WHT % on each line.",
          has_payments: "This bill already has a payment — edits aren't allowed.",
          not_editable: "This bill is no longer editable (already approved or void).",
        };
        setErr(friendly[e.code ?? ""] ?? e.detail ?? `Could not save the bill (${e.code ?? e.status}).`);
      } else {
        setErr("Could not save bill.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Bills</div>
          <h1 className="stage-title">
            New <em>bill.</em>
          </h1>
          <p className="stage-sub">
            Manual entry for now. Receipt capture (photo → OCR → draft) ships in week 8 once
            Textract + Claude Haiku are wired up.
          </p>
        </div>
        <Link to="/bills" className="skip-link">← Back</Link>
      </div>

      <form onSubmit={submit} className="card card-pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-row">
          <div className="form-field">
            <label className="form-label">Supplier</label>
            <select
              className="form-input filled"
              value={supplierId}
              onChange={(e) => onSupplierChange(e.target.value)}
              required
            >
              <option value="">— pick a supplier —</option>
              {suppliersData?.suppliers.map((s: Supplier) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Bill number (supplier's)</label>
            <input className="form-input filled" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-label">Issue date</label>
            <input className="form-input filled" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Due date</label>
            <input className="form-input filled" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 6 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--ink-2)" }}>
            <input type="checkbox" checked={taxInvoiceReceived} onChange={(e) => setTaxInvoiceReceived(e.target.checked)} />
            Tax invoice received (unlocks input-VAT claim)
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          {lines.map((l, i) => (
            <div key={i} className="card" style={{ padding: 14, background: "var(--paper)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 130px 110px 110px auto", gap: 8, alignItems: "end" }}>
                <div className="form-field">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={l.description} onChange={(e) => update(i, { description: e.target.value })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Qty</label>
                  <input className="form-input num" type="number" step="0.01" value={l.quantity} onChange={(e) => update(i, { quantity: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Unit ({tenant?.base_currency ?? "LKR"})</label>
                  <RupeeInput cents={Number(l.unitPriceCents)} onChange={(c) => update(i, { unitPriceCents: c })} />
                </div>
                <div className="form-field">
                  <label className="form-label">VAT %</label>
                  <input className="form-input num" type="number" step="0.01" value={l.vatRatePct ?? ""} onChange={(e) => update(i, { vatRatePct: e.target.value ? parseFloat(e.target.value) : null })} />
                </div>
                <div className="form-field">
                  <label className="form-label">WHT % (we withhold)</label>
                  <input className="form-input num" type="number" step="0.01" value={l.whtRatePct ?? ""} onChange={(e) => update(i, { whtRatePct: e.target.value ? parseFloat(e.target.value) : null })} />
                </div>
                <button type="button" className="btn btn-ghost" onClick={() => removeLine(i)} disabled={lines.length === 1}>×</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr auto", gap: 8, alignItems: "end", marginTop: 8 }}>
                <div className="form-field">
                  <label className="form-label">VAT classification (input)</label>
                  <select
                    className="form-input"
                    value={l.vatClassification ?? "taxable"}
                    onChange={(e) => {
                      const next = e.target.value as BillLineInput["vatClassification"];
                      const prevCarries = l.vatClassification === "taxable" || l.vatClassification === "imported";
                      const nextCarries = next === "taxable" || next === "imported";
                      // Force-repopulate when classification crosses the
                      // VAT-carries boundary (e.g. exempt → taxable). Keeps
                      // the rate the user typed if they're just refining
                      // within "taxable" / "imported".
                      const classificationChanged = prevCarries !== nextCarries;
                      update(i, {
                        vatClassification: next,
                        vatRatePct: nextCarries
                          ? (classificationChanged ? (tenant?.vat_registered ? 18 : null) : l.vatRatePct)
                          : 0,
                        // SSCL follows VAT — supplier charges SSCL only on
                        // standard taxable supplies (Shamil 2026-06).
                        ssclApply: nextCarries && !!tenant?.sscl_liable,
                      });
                    }}
                  >
                    {VAT_INPUT_CLASSIFICATIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                {(() => {
                  const carries = l.vatClassification === "taxable" || l.vatClassification === "imported" || l.vatClassification == null;
                  const mandatory = carries && !!tenant?.sscl_liable && !!tenant?.vat_registered;
                  const notApplicable = !carries;
                  const disabled = mandatory || notApplicable || !tenant?.sscl_liable;
                  const hint = mandatory
                    ? "Supplier-charged SSCL on taxable purchase"
                    : notApplicable
                      ? "Not applicable"
                      : !tenant?.sscl_liable
                        ? "Tenant not SSCL-liable"
                        : "";
                  return (
                    <label
                      style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12, color: "var(--ink-3)" }}
                      title={hint}
                    >
                      <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={!!l.ssclApply}
                          onChange={(e) => update(i, { ssclApply: e.target.checked })}
                          disabled={disabled}
                        />
                        SSCL 2.5%
                      </span>
                      {hint && <span style={{ fontSize: 10, color: "var(--ink-4)" }}>{hint}</span>}
                    </label>
                  );
                })()}
              </div>
              {lineErrors(l).length > 0 && (
                <ul style={{
                  marginTop: 10, padding: "8px 12px", listStyle: "none",
                  background: "var(--rose-soft)", color: "var(--rose)",
                  borderRadius: 6, fontSize: 12, display: "flex", flexDirection: "column", gap: 4,
                }}>
                  {lineErrors(l).map((msg, j) => <li key={j}>· {msg}</li>)}
                </ul>
              )}
            </div>
          ))}
          <button className="btn btn-line" type="button" onClick={addLine} style={{ alignSelf: "flex-start" }}>
            + Add line
          </button>
        </div>

        <div className="card" style={{ padding: 16, background: "var(--paper-2)", marginTop: 8 }}>
          <Row label="Subtotal" value={fmtCents(totals.sub.toString())} />
          <Row label="VAT" value={fmtCents(totals.vat.toString())} />
          <Row label="SSCL" value={fmtCents(totals.sscl.toString())} />
          <Row label="WHT (we withhold)" value={`-${fmtCents(totals.wht.toString())}`} />
          <Row label="Total" value={fmtCents(totals.total.toString())} bold />
        </div>

        {err && <div className="banner banner-error">{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Link to="/bills" className="btn btn-ghost">Cancel</Link>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={submitting || !supplierId || !billNumber || anyLineHasErrors}
            title={anyLineHasErrors ? "Fix the highlighted line(s) before saving." : ""}
          >
            {submitting ? "Saving…" : "Save bill →"}
          </button>
        </div>
      </form>
    </>
  );
}

function BillEdit() {
  const navigate = useNavigate();
  // Pick the id by searching for the "bills" segment instead of relying on
  // a fixed positional index — location.pathname can be prefixed with the
  // SPA basename in some runtime states.
  const parts = location.pathname.split("/");
  const i = parts.indexOf("bills");
  const id = i >= 0 ? parts[i + 1] : "";
  const { data: bill, isLoading } = useQuery({
    queryKey: ["bill", id],
    queryFn: () => billsApi.get(id),
  });
  const { data: tenant } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const [lines, setLines] = useState<BillLineInput[]>([]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!bill) return;
    setNotes(bill.notes ?? "");
    setLines(
      bill.lines.map((l) => ({
        description: l.description,
        lineType: (l.line_type ?? "service") as BillLineInput["lineType"],
        quantity: Number(l.quantity),
        unitPriceCents: Number(l.unit_price_cents),
        vatClassification: (l.vat_classification ?? "taxable") as BillLineInput["vatClassification"],
        vatRatePct: l.vat_rate_pct != null ? Number(l.vat_rate_pct) : null,
        ssclApply: Number(l.sscl_amount_cents ?? "0") > 0,
        whtRatePct: l.wht_rate_pct != null ? Number(l.wht_rate_pct) : null,
      })),
    );
  }, [bill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !bill) return <div className="card">Loading…</div>;
  if (bill.status !== "pending_approval" || BigInt(bill.paid_cents ?? "0") !== 0n) {
    return (
      <div className="card card-pad-lg">
        <h3 style={{ fontFamily: "'Fraunces', serif" }}>Can't edit</h3>
        <p style={{ color: "var(--ink-3)" }}>
          This bill is {bill.status}. To change anything after approval you'll
          need to reject and re-enter it.
        </p>
        <Link to={`/bills/${bill.id}`} className="btn btn-line">Back to bill</Link>
      </div>
    );
  }

  function update(i: number, patch: Partial<BillLineInput>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines([
      ...lines,
      {
        description: "", lineType: "service", quantity: 1, unitPriceCents: 0,
        vatClassification: "taxable", vatRatePct: tenant?.vat_registered ? 18 : null,
        ssclApply: !!tenant?.sscl_liable && !!tenant?.vat_registered, whtRatePct: null,
      },
    ]);
  }
  function removeLine(i: number) {
    if (lines.length === 1) return;
    setLines(lines.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSubmitting(true);
    setErr(null);
    try {
      await billsApi.update(id, {
        supplierId: bill!.supplier.id,
        billNumber: bill!.bill_number,
        issueDate: bill!.issue_date.slice(0, 10),
        dueDate: bill!.due_date.slice(0, 10),
        currency: bill!.currency,
        notes: notes || null,
        taxInvoiceReceived: !!bill!.tax_invoice_received_at,
        lines: lines.map((l) => ({ ...l, unitPriceCents: String(l.unitPriceCents) })),
      });
      navigate(`/bills/${id}`);
    } catch (e) {
      if (e instanceof ApiError) {
        const friendly: Record<string, string> = {
          has_payments: "This bill already has a payment — edits aren't allowed.",
          not_editable: "This bill is no longer editable.",
          wht_threshold_not_crossed: "WHT can't be charged: the supplier hasn't crossed the Rs 100,000 monthly threshold yet. Clear the WHT % on each line.",
        };
        setErr(friendly[e.code ?? ""] ?? e.detail ?? `Could not save (${e.code ?? e.status}).`);
      } else {
        setErr("Could not save bill.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const totals = lines.reduce(
    (acc, l) => {
      const qtyT = BigInt(Math.round((l.quantity || 0) * 1000));
      const price = BigInt(l.unitPriceCents || 0);
      const sub = (price * qtyT) / 1000n;
      const vat = l.vatRatePct ? (sub * BigInt(Math.round(l.vatRatePct * 100))) / 10000n : 0n;
      const sscl = l.ssclApply ? (sub * 25n) / 1000n : 0n;
      const wht = l.whtRatePct ? (sub * BigInt(Math.round(l.whtRatePct * 100))) / 10000n : 0n;
      return {
        sub: acc.sub + sub, vat: acc.vat + vat, sscl: acc.sscl + sscl, wht: acc.wht + wht,
        total: acc.total + sub + vat + sscl - wht,
      };
    },
    { sub: 0n, vat: 0n, sscl: 0n, wht: 0n, total: 0n },
  );

  return (
    <>
      <div className="stage-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 className="stage-title">Edit {bill.bill_number}</h1>
          <p className="stage-sub">
            Pending-approval bill for {bill.supplier.name}. Supplier and dates lock once created — reject and re-enter to change those.
          </p>
        </div>
        <Link to={`/bills/${bill.id}`} className="btn btn-ghost">Cancel</Link>
      </div>

      <div className="card card-pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lines.map((l, i) => (
            <div key={i} className="card" style={{ padding: 14, background: "var(--paper)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 130px 110px 110px auto", gap: 8, alignItems: "end" }}>
                <div className="form-field">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={l.description} onChange={(e) => update(i, { description: e.target.value })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Qty</label>
                  <input className="form-input num" type="number" step="0.01" value={l.quantity} onChange={(e) => update(i, { quantity: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Unit ({tenant?.base_currency ?? "LKR"})</label>
                  <RupeeInput cents={Number(l.unitPriceCents)} onChange={(c) => update(i, { unitPriceCents: c })} />
                </div>
                <div className="form-field">
                  <label className="form-label">VAT %</label>
                  <input className="form-input num" type="number" step="0.01" value={l.vatRatePct ?? ""} onChange={(e) => update(i, { vatRatePct: e.target.value ? parseFloat(e.target.value) : null })} />
                </div>
                <div className="form-field">
                  <label className="form-label">WHT %</label>
                  <input className="form-input num" type="number" step="0.01" value={l.whtRatePct ?? ""} onChange={(e) => update(i, { whtRatePct: e.target.value ? parseFloat(e.target.value) : null })} />
                </div>
                <button type="button" className="btn btn-ghost" onClick={() => removeLine(i)} disabled={lines.length === 1}>×</button>
              </div>
              <div className="form-field" style={{ marginTop: 8 }}>
                <label className="form-label">VAT classification</label>
                <select
                  className="form-input"
                  value={l.vatClassification ?? "taxable"}
                  onChange={(e) => {
                    const next = e.target.value as BillLineInput["vatClassification"];
                    const prevCarries = l.vatClassification === "taxable" || l.vatClassification === "imported";
                    const nextCarries = next === "taxable" || next === "imported";
                    const classificationChanged = prevCarries !== nextCarries;
                    update(i, {
                      vatClassification: next,
                      vatRatePct: nextCarries
                        ? (classificationChanged ? (tenant?.vat_registered ? 18 : null) : l.vatRatePct)
                        : 0,
                      ssclApply: nextCarries && !!tenant?.sscl_liable,
                    });
                  }}
                >
                  {VAT_INPUT_CLASSIFICATIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
          <button className="btn btn-line" type="button" onClick={addLine} style={{ alignSelf: "flex-start" }}>
            + Add line
          </button>
        </div>

        <div className="card" style={{ padding: 16, background: "var(--paper-2)" }}>
          <Row label="Subtotal" value={fmtCents(totals.sub.toString())} />
          <Row label="VAT" value={fmtCents(totals.vat.toString())} />
          <Row label="SSCL" value={fmtCents(totals.sscl.toString())} />
          <Row label="WHT (we withhold)" value={`-${fmtCents(totals.wht.toString())}`} />
          <Row label="Total" value={fmtCents(totals.total.toString())} bold />
        </div>

        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {err && <div className="banner banner-error">{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Link to={`/bills/${bill.id}`} className="btn btn-ghost">Cancel</Link>
          <button className="btn btn-primary" onClick={save} disabled={submitting}>
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </>
  );
}

function BillDetail() {
  const id = location.pathname.split("/").pop()!;
  const { data, isLoading } = useQuery({ queryKey: ["bill", id], queryFn: () => billsApi.get(id) });
  const qc = useQueryClient();
  const [paying, setPaying] = useState(false);

  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;
  const b = data;

  async function approve() {
    await billsApi.approve(b.id);
    qc.invalidateQueries({ queryKey: ["bill", b.id] });
  }
  async function reject() {
    const reason = prompt("Reason for rejection?");
    if (reason !== null) {
      await billsApi.reject(b.id, reason);
      qc.invalidateQueries({ queryKey: ["bill", b.id] });
    }
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Bill {b.bill_number}</div>
          <h1 className="stage-title">
            {b.supplier.name}<em>.</em>
          </h1>
          <p className="stage-sub">Issued {fmtDate(b.issue_date)} · Due {fmtDate(b.due_date)}</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusPill
            status={b.status}
            dueDate={b.due_date}
            paidCents={b.paid_cents}
            totalCents={b.total_cents}
            size="md"
          />
          {b.status === "pending_approval" && BigInt(b.paid_cents ?? "0") === 0n && (
            <>
              <Link className="btn btn-line" to={`/bills/${b.id}/edit`}>Edit</Link>
              <button className="btn btn-line" onClick={reject}>Reject</button>
              <button className="btn btn-primary" onClick={approve}>Approve →</button>
            </>
          )}
          {b.status === "approved" && (
            <button className="btn btn-primary" disabled={paying} onClick={() => setPaying(true)}>
              Record payment
            </button>
          )}
        </div>
      </div>

      <div className="card card-pad-lg" style={{ padding: 0 }}>
        <div className="table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Description</th>
              <th style={{ ...th, textAlign: "right" }}>Qty</th>
              <th style={{ ...th, textAlign: "right" }}>Unit</th>
              <th style={{ ...th, textAlign: "right" }}>Subtotal</th>
              <th style={{ ...th, textAlign: "right" }}>VAT</th>
              <th style={{ ...th, textAlign: "right" }}>WHT</th>
            </tr>
          </thead>
          <tbody>
            {b.lines.map((l) => (
              <tr key={l.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                <td style={{ padding: "14px 18px", fontSize: 13 }}>{l.description}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">{l.quantity}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">{fmtCents(l.unit_price_cents)}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right", fontWeight: 500 }} className="num">{fmtCents(l.line_subtotal_cents)}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">{fmtCents(l.vat_amount_cents)}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">{fmtCents(l.wht_amount_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="bill-totals-grid" style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
          <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{b.notes ?? ""}</div>
          <div>
            <Row label="Subtotal" value={fmtCents(b.subtotal_cents)} />
            <Row label="VAT" value={fmtCents(b.vat_total_cents)} />
            <Row label="WHT" value={`-${fmtCents(b.wht_total_cents)}`} />
            <Row label="Total" value={fmtCents(b.total_cents)} bold />
            <Row label="Paid" value={fmtCents(b.paid_cents)} />
            {/* Shamil 2026-06-24: show balance payable after the partial. */}
            {BigInt(b.paid_cents ?? "0") > 0n && BigInt(b.paid_cents ?? "0") < BigInt(b.total_cents ?? "0") && (
              <Row
                label="Balance payable"
                value={fmtCents(
                  String(BigInt(b.total_cents ?? "0") - BigInt(b.paid_cents ?? "0")),
                )}
                bold
              />
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}>
        <BillPaymentsList billId={b.id} />
        <AttachmentList entityType="bill" entityId={b.id} readOnly={b.status === "void"} />
      </div>

      <HistoryDrawer entityType="bill" entityId={b.id} />

      {paying && (
        <PaymentModal
          billId={b.id}
          remaining={b.total_cents}
          onClose={() => setPaying(false)}
          onPaid={() => {
            setPaying(false);
            qc.invalidateQueries({ queryKey: ["bill", b.id] });
          }}
        />
      )}
    </>
  );
}

function PaymentModal({
  billId,
  remaining,
  onClose,
  onPaid,
}: {
  billId: string;
  remaining: string;
  onClose: () => void;
  onPaid: () => void;
}) {
  // `amount` is held as a number of cents (matches DB units). The UI input
  // is a RupeeInput so the user types "100,000.00" not "10000000".
  const [amountCents, setAmountCents] = useState<number>(Number(remaining));
  const [date, setDate] = useState(todayLocalISO());
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (amountCents <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (amountCents > Number(remaining)) {
      setError(`Amount exceeds the remaining balance (${fmtCents(remaining)}).`);
      return;
    }
    setSubmitting(true);
    try {
      const { paymentsApi } = await import("../lib/business");
      await paymentsApi.create({
        direction: "out",
        billId,
        paymentDate: date,
        amountCents: String(amountCents),
        reference: reference || null,
      });
      onPaid();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record payment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(14,22,18,0.5)",
        display: "grid", placeItems: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card card-pad-lg modal-card"
        style={{ width: 420, maxWidth: "90vw", display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500 }}>
          Record payment
        </div>
        <div className="form-field">
          <label className="form-label">Amount (LKR)</label>
          <RupeeInput cents={amountCents} onChange={setAmountCents} />
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
            Outstanding: {fmtCents(remaining)}
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Date</label>
          <input className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-label">Reference</label>
          <input className="form-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. cheque no, bank transfer ref" />
        </div>
        {error && <div className="banner banner-error">{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : "Record →"}
          </button>
        </div>
      </form>
    </div>
  );
}

const billTabsStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  padding: 4,
  background: "var(--paper-2)",
  borderRadius: 100,
  border: "0.5px solid var(--line)",
  marginBottom: 20,
  width: "fit-content",
};
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 18px",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  background: "var(--paper)",
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "14px 18px",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        fontSize: 13,
        fontWeight: bold ? 500 : 400,
        color: bold ? "var(--ink)" : "var(--ink-3)",
        borderTop: bold ? "0.5px solid var(--ink)" : undefined,
        marginTop: bold ? 4 : 0,
        paddingTop: bold ? 8 : 6,
      }}
    >
      <span>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString("en-LK", { day: "2-digit", month: "short", year: "numeric" });
}

// Payments allocated to a bill — date in small font + Reverse action.
// Shamil 2026-06-24.
function BillPaymentsList({ billId }: { billId: string }) {
  const qc = useQueryClient();
  const pq = useQuery({
    queryKey: ["payments", "bill", billId],
    queryFn: () => paymentsApi.list({ billId }),
  });
  const banksQ = useQuery({ queryKey: ["bank-accounts"], queryFn: () => bankApi.listAccounts() });
  const banksById = Object.fromEntries((banksQ.data?.accounts ?? []).map((b) => [b.id, b.accountName]));
  const reverseM = useMutation({
    mutationFn: (id: string) => paymentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments", "bill", billId] });
      qc.invalidateQueries({ queryKey: ["bill", billId] });
      qc.invalidateQueries({ queryKey: ["history", "bill", billId] });
    },
  });
  const payments = pq.data?.payments ?? [];
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)", margin: 0 }}>Payments</h4>
        {payments.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{payments.length} recorded</span>
        )}
      </div>
      {pq.isLoading && <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>}
      {!pq.isLoading && payments.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>No payments recorded yet.</div>
      )}
      {payments.map((p) => (
        <div key={p.id} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 0", borderTop: "0.5px solid var(--line)", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }} className="num">
              {fmtCents(p.amount_cents)}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>
              {new Date(p.payment_date).toLocaleDateString()}
              {p.bank_account_id && banksById[p.bank_account_id] && (
                <> · {banksById[p.bank_account_id]}</>
              )}
              {p.reference && <> · {p.reference}</>}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 11, padding: "4px 8px", color: "var(--rose, #b91c1c)" }}
            disabled={reverseM.isPending}
            onClick={() => {
              if (confirm(`Reverse this ${fmtCents(p.amount_cents)} payment? The ledger entry will be reversed and the bill balance restored.`)) {
                reverseM.mutate(p.id);
              }
            }}
          >
            {reverseM.isPending ? "…" : "Reverse"}
          </button>
        </div>
      ))}
    </div>
  );
}
